import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useAuth } from '../../contexts/AuthContext';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import { useTranslation } from 'react-i18next';



import PatientDemographicsForm from './components/PatientDemographicsForm';
import ClinicalPresentationForm from './components/ClinicalPresentationForm';
import BodyRegionSelector from './components/BodyRegionSelector';
import AIRecommendationDisplay from './components/AIRecommendationDisplay';
import Button from '../../components/ui/Button';


import { profileService } from '../../services/profileService';
import anthropicService from '../../services/anthropicService';
import Icon from '../../components/AppIcon';
import { saveRecommendation } from '../../services/recommendationService';
import { matchRecommendationToIrefer } from '../../services/ireferService';
import { generateRecommendationPDF } from '../../utils/pdfGenerator';
import { patientService } from '../../services/patientService';



const ClinicalRecommendation = () => {
  const navigate = useNavigate();
  const { profile, updateProfile } = useAuth();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [userProfile, setUserProfile] = useState(null);
  const [creditsRemaining, setCreditsRemaining] = useState(0);
  const [formData, setFormData] = useState({
    patientName: '',
    contactNumber: '',
    patientEmail: '',
    patientId: '',
    patientAge: '',
    gender: '',
    weight: '',
    isEmergency: false,
    isPregnant: false,
    hasPacemaker: false,
    hasMetalImplants: false,
    metalImplantTypes: [],
    chiefComplaint: '',
    symptomDuration: '',
    severity: '',
    clinicalHistory: '',
    examinationFindings: ''
  });

  const [selectedRegion, setSelectedRegion] = useState('');
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [creditCost] = useState(5);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  // Add ref for results section
  const resultsRef = useRef(null);

  // Fetch user profile on component mount
  useEffect(() => {
    // Scroll to top when step changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Check if user has enough credits (admin always has unlimited)
  const hasEnoughCredits = profile?.role === 'admin' || (profile?.credits_remaining || 0) >= creditCost;

  // Check if user has sufficient credits
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (profile?.id) {
        try {
          const profileData = await profileService?.getProfile(profile?.id);
          setUserProfile(profileData);
          setCreditsRemaining(profileData?.credits_remaining || 0);
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };

    fetchUserProfile();
  }, [profile]);

  // Scroll to results when recommendation is generated
  useEffect(() => {
    if (recommendation && resultsRef?.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        resultsRef?.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
        
        // Additional offset to account for sticky header
        const yOffset = -100; // Adjust based on your navigation height
        const element = resultsRef?.current;
        const y = element?.getBoundingClientRect()?.top + window?.pageYOffset + yOffset;
        
        window?.scrollTo({ top: y, behavior: 'smooth' });
      }, 200);
    }
  }, [recommendation]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.patientName?.trim()) {
      newErrors.patientName = 'Patient name is required';
    }
    if (!formData?.contactNumber?.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    }
    if (!formData?.patientId?.trim()) {
      newErrors.patientId = 'Patient ID is required';
    }
    if (!formData?.patientAge || formData?.patientAge <= 0) {
      newErrors.patientAge = 'Valid patient age is required';
    }
    if (!formData?.gender) {
      newErrors.gender = 'Gender is required';
    }
    if (!formData?.chiefComplaint?.trim()) {
      newErrors.chiefComplaint = 'Chief complaint is required';
    }
    if (!formData?.symptomDuration) {
      newErrors.symptomDuration = 'Symptom duration is required';
    }
    if (!formData?.severity) {
      newErrors.severity = 'Symptom severity is required';
    }
    if (!formData?.clinicalHistory?.trim()) {
      newErrors.clinicalHistory = 'Clinical history is required';
    }
    if (!selectedRegion) {
      newErrors.region = 'Body region selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Helper function to determine age group from numeric age
  const getAgeGroup = (age) => {
    const numericAge = parseInt(age);
    if (numericAge < 0.077) return 'Neonate (0-28 days)'; // Less than 28 days
    if (numericAge < 1) return 'Infant (1-12 months)';
    if (numericAge <= 12) return 'Child (1-12 years)';
    if (numericAge <= 17) return 'Adolescent (13-17 years)';
    if (numericAge <= 64) return 'Adult (18-64 years)';
    return 'Elderly (65+ years)';
  };

  const generateClinicalRecommendation = async (formData) => {
    try {
      // Prepare clinical data for AI analysis
      const clinicalData = {
        patientId: formData?.patientId,
        patientAge: formData?.patientAge,
        ageGroup: getAgeGroup(formData?.patientAge),
        gender: formData?.gender,
        weight: formData?.weight,
        isEmergency: formData?.isEmergency,
        isPregnant: formData?.isPregnant,
        chiefComplaint: formData?.chiefComplaint,
        symptomDuration: formData?.symptomDuration,
        severity: formData?.severity,
        clinicalHistory: formData?.clinicalHistory,
        examinationFindings: formData?.examinationFindings,
        selectedRegion: selectedRegion
      };

      // Call Anthropic AI service to generate recommendation
      const aiRecommendation = await anthropicService?.generateClinicalRecommendation(clinicalData);
      
      return aiRecommendation;
    } catch (error) {
      console.error('Error generating recommendation:', error);
      throw error;
    }
  };

  const handleGenerateRecommendation = async () => {
    // CRITICAL: Validate form BEFORE any operations
    if (!validateForm()) {
      setApiError({
        type: 'validation',
        message: 'Please fill in all required fields before generating a recommendation.'
      });
      
      // Scroll to first error
      const firstError = document.querySelector('[data-error="true"]');
      if (firstError) {
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Check credits before generating
    if (!hasEnoughCredits) {
      // User requested to never display insufficient credits message
      // Silently prevent generation without showing error
      setIsGenerating(false);
      return;
    }

    setIsGenerating(true);
    setIsComplete(false);
    setApiError(null);
    setRecommendation(null);

    try {
      // Save or update patient data before generating recommendation
      if (formData?.patientName && formData?.contactNumber) {
        try {
          await patientService?.createPatient({
            patientName: formData?.patientName,
            contactNumber: formData?.contactNumber,
            email: formData?.patientEmail || null,
            dateOfBirth: formData?.dateOfBirth || null,
            gender: formData?.gender || null
          });
        } catch (patientError) {
          // If patient already exists or other error, continue with recommendation
          console.log('Patient save note:', patientError?.message);
        }
      }

      const response = await generateClinicalRecommendation(formData);
      
      if (response?.error) {
        setApiError({
          type: 'error',
          message: response?.error || 'Failed to generate recommendation'
        });
        return;
      }

      setRecommendation({
        text: response,
        timestamp: new Date()?.toISOString(),
        patientInfo: formData,
        ...response
      });

      // Mark as complete for green button state
      setIsComplete(true);

      // Save recommendation to history
      try {
        const savedRec = await saveRecommendation(profile?.id, {
          // Patient demographics
          patientId: formData?.patientId,
          patientAge: formData?.patientAge,
          patientGender: formData?.gender,
          weight: formData?.weight,
          isEmergency: formData?.isEmergency,
          isPregnant: formData?.isPregnant,
          // Clinical presentation
          chiefComplaint: formData?.chiefComplaint,
          symptomDuration: formData?.symptomDuration,
          severity: formData?.severity,
          clinicalHistory: formData?.clinicalHistory,
          examinationFindings: formData?.examinationFindings,
          bodyRegion: selectedRegion,
          clinicalPresentation: formData?.chiefComplaint,
          // AI recommendation results
          primaryModality: response?.primaryModality,
          recommendedModality: response?.primaryModality || response?.recommendedModality || 'N/A',
          modalityRationale: response?.modalityRationale,
          clinicalRationale: response?.clinicalRationale,
          reasoning: response?.clinicalRationale || response?.reasoning || 'N/A',
          // Evidence and guidelines
          evidenceGrade: response?.evidenceGrade,
          urgency: response?.urgency,
          confidenceScore: response?.confidenceScore || 0.85,
          alternativeModalities: response?.alternativeModalities || [],
          guidelinesReferenced: response?.guidelinesReferenced || [],
          iReferGuidelines: response?.iReferGuidelines || [],
          // Safety and radiation
          radiationDose: response?.radiationDose,
          contrastRequired: response?.contrastRequired || false,
          contrastDetails: response?.contrastDetails,
          safetyWarnings: response?.safetyWarnings || [],
          // Complete AI response for full detail view
          fullAiResponse: response,
          creditsUsed: 1
        });

        // NEW: Match recommendation to iRefer guideline
        const ireferMatch = await matchRecommendationToIrefer({
          bodyRegion: selectedRegion,
          clinicalPresentation: formData?.chiefComplaint + ' ' + formData?.clinicalHistory,
          reasoning: response?.clinicalRationale || response?.reasoning || '',
          recommendedModality: response?.primaryModality || response?.recommendedModality || ''
        });

        // Add iRefer data to recommendation display
        if (ireferMatch) {
          setRecommendation(prev => ({
            ...prev,
            ireferCode: ireferMatch?.ireferCode,
            ireferUrl: ireferMatch?.ireferUrl,
            ireferConfidence: ireferMatch?.confidence,
            id: savedRec?.id
          }));
        } else {
          setRecommendation(prev => ({
            ...prev,
            id: savedRec?.id
          }));
        }
      } catch (saveError) {
        console.error('Error saving recommendation to history:', saveError);
      }

      // CRITICAL: Deduct 1 credit (not 5)
      const newCreditsRemaining = (profile?.credits_remaining || 0) - 1;
      await updateProfile({ credits_remaining: newCreditsRemaining });
      
      setSuccessMessage({
        message: 'Recommendation generated successfully! Credits remaining: ' + newCreditsRemaining
      });
    } catch (err) {
      console.error('Error generating recommendation:', err);
      
      // Enhanced error handling with specific error types
      let errorMessage = 'Failed to generate recommendation. Please try again.';
      let errorType = 'error';
      
      if (err?.message?.includes('API key')) {
        errorMessage = 'Invalid Anthropic API key. Please check your configuration in the .env file.';
        errorType = 'config';
      } else if (err?.message?.includes('insufficient credits') || err?.message?.includes('insufficient_quota')) {
        errorMessage = 'Your Anthropic API account has insufficient credits. Please add credits at console.anthropic.com';
        errorType = 'credit';
      } else if (err?.message?.includes('Rate limit')) {
        errorMessage = 'API rate limit exceeded. Please wait a moment before trying again.';
        errorType = 'rate_limit';
      } else if (err?.message?.includes('temporarily unavailable')) {
        errorMessage = 'Anthropic service is temporarily unavailable. Please try again in a few minutes.';
        errorType = 'service';
      } else if (err?.message) {
        errorMessage = err?.message;
      }
      
      setApiError({
        type: errorType,
        message: errorMessage
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveRecommendation = () => {
    console.log('Saving recommendation:', recommendation);
    alert('Recommendation saved successfully!');
  };

  const handleCopyRecommendation = () => {
    const text = `Clinical Recommendation\n\nPrimary Modality: ${recommendation?.primaryModality}\nEvidence Grade: ${recommendation?.evidenceGrade}\nUrgency: ${recommendation?.urgency}\n\nRationale: ${recommendation?.clinicalRationale}`;
    navigator.clipboard?.writeText(text);
    alert('Recommendation copied to clipboard!');
  };

  const handleExportPDF = async () => {
    if (!recommendation || !formData) {
      alert('No recommendation available to export');
      return;
    }

    try {
      setIsExportingPDF(true);

      // Get physician information from profile
      const physicianInfo = {
        full_name: profile?.full_name,
        license_number: profile?.license_number,
        specialization: profile?.specialization,
        institution_name: profile?.institution_name,
        institution_country: profile?.institution_country,
        email: profile?.email,
        years_of_practice: profile?.years_of_practice
      };

      const result = await generateRecommendationPDF(
        recommendation,
        {
          patientId: formData?.patientId,
          patientAge: formData?.patientAge,
          gender: formData?.gender,
          weight: formData?.weight,
          isEmergency: formData?.isEmergency,
          isPregnant: formData?.isPregnant,
          chiefComplaint: formData?.chiefComplaint,
          symptomDuration: formData?.symptomDuration,
          severity: formData?.severity,
          clinicalHistory: formData?.clinicalHistory,
          examinationFindings: formData?.examinationFindings
        },
        selectedRegion,
        physicianInfo
      );

      if (result?.success) {
        alert(t('clinicalRecommendation.pdfSuccess', { fileName: result?.fileName }) || 'PDF exported successfully!');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      alert(t('clinicalRecommendation.pdfError') || 'Error exporting PDF. Please try again.');
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleReset = () => {
    setFormData({
      patientId: '',
      patientAge: '',
      gender: '',
      weight: '',
      isEmergency: false,
      isPregnant: false,
      chiefComplaint: '',
      symptomDuration: '',
      severity: '',
      clinicalHistory: '',
      examinationFindings: ''
    });
    setSelectedRegion('');
    setErrors({});
    setRecommendation(null);
    setApiError(null);
    setSuccessMessage(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation userRole={profile?.role} />
      <main className="pt-header">
        <div className="container mx-auto container-padding py-6 sm:py-8 md:py-12 max-w-7xl">
          {/* Header Section */}
          <div className="mb-6 sm:mb-8 md:mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-2">
                  {t('clinicalRecommendation.title')}
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  {t('clinicalRecommendation.subtitle')}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20">
                <Icon name="Zap" size={20} color="var(--color-primary)" />
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">{t('clinicalRecommendation.creditsRemaining')}</p>
                  <p className="text-lg sm:text-xl font-bold text-foreground">{profile?.credits_remaining || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-green-800 mb-1">
                    {t('clinicalRecommendation.recommendationGenerated')}
                  </h3>
                  <p className="text-sm text-green-700">
                    {successMessage?.message}
                  </p>
                  <button
                    onClick={() => setSuccessMessage(null)}
                    className="mt-2 text-sm font-medium text-green-600 hover:text-green-800 transition-colors"
                  >
                    {t('clinicalRecommendation.dismiss')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {apiError && (
            <div className="mb-6 p-4 bg-destructive/10 border-l-4 border-destructive rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-destructive" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-destructive mb-1">
                    {apiError?.type === 'credit' ? t('clinicalRecommendation.apiCreditsRequired') : t('clinicalRecommendation.errorGenerating')}
                  </h3>
                  <p className="text-sm text-destructive/90">
                    {apiError?.message}
                  </p>
                  {apiError?.type === 'credit' && (
                    <div className="mt-3 flex items-center gap-3">
                      <a 
                        href="https://console.anthropic.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-destructive hover:text-destructive/80 transition-colors"
                      >
                        {t('clinicalRecommendation.addCreditsToAccount')}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <button
                        onClick={() => setApiError(null)}
                        className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {t('clinicalRecommendation.dismiss')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6">
            {/* Full Width Form Section on Top */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PatientDemographicsForm 
                  formData={formData}
                  onFormChange={handleFormChange}
                  errors={errors}
                />

                <ClinicalPresentationForm 
                  formData={formData}
                  onFormChange={handleFormChange}
                  errors={errors}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <BodyRegionSelector 
                  selectedRegion={selectedRegion}
                  onRegionChange={setSelectedRegion}
                />
              </div>

              {errors?.region && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive">{errors?.region}</p>
                </div>
              )}

              <div className="flex items-center justify-center">
                <Button 
                  variant="default" 
                  size="lg" 
                  iconName={isComplete ? "Check" : "Sparkles"}
                  iconPosition="left"
                  onClick={handleGenerateRecommendation}
                  loading={isGenerating}
                  disabled={isGenerating || isComplete}
                  className={`
                    min-w-[300px] transition-all duration-300
                    ${isComplete ? 'bg-success hover:bg-success text-white' : ''}
                  `}
                >
                  {isGenerating ? t('clinicalRecommendation.generating') : isComplete ? t('clinicalRecommendation.done') : t('clinicalRecommendation.generateRecommendation')}
                </Button>
              </div>
            </div>

            {/* Full Width Results Section Below */}
            {recommendation && (
              <div ref={resultsRef} className="w-full">
                <AIRecommendationDisplay 
                  recommendation={recommendation}
                  isLoading={isGenerating}
                  onSave={handleSaveRecommendation}
                  onCopy={handleCopyRecommendation}
                  onExportPDF={handleExportPDF}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClinicalRecommendation;